"use client";

import {
   Sidebar,
   SidebarContent,
   SidebarFooter,
   SidebarMenu,
   SidebarMenuButton,
   SidebarMenuItem,
} from "@/Components/ui/sidebar";
import {
   ChevronDown,
   ChevronUp,
   Copy,
   Folder,
   Image,
   MoreHorizontal,
   Plus,
   Settings,
   Trash2,
   User,
} from "lucide-react";
import { useState } from "react";
import { useAdminStore } from "../../../store/useAdminStore";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import fetchDb from "@/utils/customRequestFunction";
import { v4 as uuidv4 } from "uuid";

export type AdminSidebarProps = {};
export function AdminSidebar({}: AdminSidebarProps) {
   const [showAllProjects, setShowAllProjects] = useState(false);
   const [hoveredProductId, setHoveredProductId] = useState<number | null>(
      null
   );
   const adminStore = useAdminStore();
   const { toast } = useToast();

   // Limit the displayed projects based on `showAllProjects`
   const displayedProducts = showAllProjects
      ? adminStore.allProducts
      : adminStore.allProducts.slice(0, 5);

   // Function to clone a product
   const handleCloneProduct = async (productId: number) => {
      try {
         toast({
            title: "Cloning product...",
            description: "Please wait while we create a copy.",
         });

         const response = await fetchDb.post(
            `/api/main-product/clone/${productId}`,
            {}
         );

         if (response) {
            // Refresh the product list
            await adminStore.fetchProducts();

            toast({
               title: "Product cloned",
               description: "The product has been successfully cloned.",
            });

            // Select the newly created product
            if (response.id) {
               adminStore.selectProduct(response.id);
            }
         }
      } catch (error) {
         console.error("Error cloning product:", error);
         toast({
            title: "Error",
            description: "Failed to clone the product. Please try again.",
            variant: "destructive",
         });
      }
   };

   // Function to delete a product
   const handleDeleteProduct = async (productId: number) => {
      if (
         !confirm(
            "Are you sure you want to delete this product? This action cannot be undone."
         )
      ) {
         return;
      }

      try {
         toast({
            title: "Deleting product...",
            description: "Please wait while we delete the product.",
         });

         await fetchDb.delete(`/api/main-product/${productId}`);

         // Refresh the product list
         await adminStore.fetchProducts();

         toast({
            title: "Product deleted",
            description: "The product has been successfully deleted.",
         });

         // If the deleted product was selected, select another one
         if (productId === adminStore.selectedProductId) {
            if (adminStore.allProducts.length > 0) {
               adminStore.selectProduct(adminStore.allProducts[0].id);
            } else {
               adminStore.selectProduct(null);
            }
         }
      } catch (error) {
         console.error("Error deleting product:", error);
         toast({
            title: "Error",
            description: "Failed to delete the product. Please try again.",
            variant: "destructive",
         });
      }
   };

   // Function to add a new product with a unique name
   const handleAddProduct = async () => {
      try {
         // Generate a unique name with UUID to prevent duplicates
         const uniqueId = uuidv4().substring(0, 8);
         const uniqueName = `New Product ${uniqueId}`;

         toast({
            title: "Creating product...",
            description: "Please wait while we create a new product.",
         });

         await adminStore.addProduct(uniqueName);

         toast({
            title: "Product created",
            description: "A new product has been created.",
         });
      } catch (error) {
         console.error("Error adding product:", error);
         toast({
            title: "Error",
            description: "Failed to create a new product. Please try again.",
            variant: "destructive",
         });
      }
   };

   return (
      <Sidebar>
         <SidebarContent>
            <SidebarMenu>
               <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                     <h2 className="text-sm font-semibold text-gray-600 mb-2">
                        Current Projects
                     </h2>
                  </SidebarMenuButton>
               </SidebarMenuItem>
               {displayedProducts.map((product) => (
                  <SidebarMenuItem key={product.id}>
                     <div
                        className="flex items-center w-full relative"
                        onMouseEnter={() =>
                           product.id && setHoveredProductId(product.id)
                        }
                     >
                        <SidebarMenuButton asChild className="flex-1">
                           <button
                              className={`w-full text-left flex items-center transition-colors duration-100 hover:bg-gray-200 ${
                                 product.id === adminStore.selectedProductId &&
                                 "bg-gray-300"
                              }`}
                              onClick={() => {
                                 if (product.id) {
                                    adminStore.selectProduct(product.id);
                                 }
                              }}
                           >
                              <Folder className="h-4 w-4 mr-2 text-gray-500" />
                              <span
                                 className={`text-sm text-gray-700 truncate`}
                              >
                                 {product.name || product.id}
                              </span>
                           </button>
                        </SidebarMenuButton>

                        {hoveredProductId === product.id && (
                           <div
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10"
                              onClick={(e) => e.stopPropagation()}
                           >
                              <DropdownMenu>
                                 <DropdownMenuTrigger>
                                    <button
                                       className="p-1 hover:bg-gray-200 rounded"
                                       onClick={(e) => e.stopPropagation()}
                                    >
                                       <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                    </button>
                                 </DropdownMenuTrigger>
                                 <DropdownMenuContent>
                                    <DropdownMenuItem
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          if (product.id)
                                             handleCloneProduct(product.id);
                                       }}
                                    >
                                       <Copy className="h-4 w-4 mr-2" />
                                       <span>Clone</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          if (product.id)
                                             handleDeleteProduct(product.id);
                                       }}
                                       className="text-red-600"
                                    >
                                       <Trash2 className="h-4 w-4 mr-2" />
                                       <span>Delete</span>
                                    </DropdownMenuItem>
                                 </DropdownMenuContent>
                              </DropdownMenu>
                           </div>
                        )}
                     </div>
                  </SidebarMenuItem>
               ))}
               {adminStore.allProducts.length > 5 && (
                  <SidebarMenuItem>
                     <SidebarMenuButton asChild>
                        <button
                           className="w-full text-left text-sm text-blue-600 hover:text-blue-800 flex items-center"
                           onClick={() => setShowAllProjects(!showAllProjects)}
                        >
                           {showAllProjects ? (
                              <>
                                 <ChevronUp className="h-4 w-4 mr-1" />
                                 Show Less
                              </>
                           ) : (
                              <>
                                 <ChevronDown className="h-4 w-4 mr-1" />
                                 Show More
                              </>
                           )}
                        </button>
                     </SidebarMenuButton>
                  </SidebarMenuItem>
               )}
               <div className="p-4 border-t border-gray-200">
                  <button
                     onClick={handleAddProduct}
                     className="flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                     <Plus className="h-5 w-5 mr-2" />
                     Add Product
                  </button>
               </div>
            </SidebarMenu>
         </SidebarContent>
         <SidebarFooter>
            <SidebarMenu>
               <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                     <button
                        className="w-full text-left flex items-center"
                        onClick={adminStore.toggleImageManagerPage}
                     >
                        <Image className="h-5 w-5 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">Images</span>
                     </button>
                  </SidebarMenuButton>
               </SidebarMenuItem>
               <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                     <button
                        className="w-full text-left flex items-center"
                        onClick={adminStore.toggleShowCategories}
                     >
                        <Folder className="h-5 w-5 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">
                           Categories
                        </span>
                     </button>
                  </SidebarMenuButton>
               </SidebarMenuItem>
               <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                     <button className="w-full text-left flex items-center">
                        <Settings className="h-5 w-5 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">Settings</span>
                     </button>
                  </SidebarMenuButton>
               </SidebarMenuItem>
               <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                     <button className="w-full text-left flex items-center">
                        <User className="h-5 w-5 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">Account</span>
                     </button>
                  </SidebarMenuButton>
               </SidebarMenuItem>
            </SidebarMenu>
         </SidebarFooter>
      </Sidebar>
   );
}
